import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';

const { t } = i18n;

interface PartnerOption {
  id: string;
  code: string;
  name: string;
}

export function TransferOrderPartnerDialog({
  orderId,
  currentPartnerId,
  onClose,
  onTransferred,
}: {
  orderId: string;
  currentPartnerId: string;
  onClose: () => void;
  onTransferred: () => void;
}) {
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [partnerId, setPartnerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<PartnerOption[]>('/partners/options')
      .then((rows) => {
        const targets = rows.filter((partner) => partner.id !== currentPartnerId);
        setPartners(targets);
      })
      .catch(() => setError(t('order.transfer.load_error')));
  }, [currentPartnerId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!partnerId) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/orders/${orderId}/partner`, {
        method: 'PUT',
        body: { partnerId },
      });
      onTransferred();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'operator_orders_not_allowed'
          ? t('order.transfer.operator_not_allowed')
          : t('order.transfer.error'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('order.transfer.title')} onClose={onClose}>
      <form onSubmit={(event) => void submit(event)} className="space-y-4 text-sm">
        <p className="text-muted-foreground">{t('order.transfer.help')}</p>
        <div className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
          {t('order.transfer.warning')}
        </div>
        <label className="block space-y-1">
          <span className="font-medium">{t('order.transfer.target')}</span>
          <select
            value={partnerId}
            onChange={(event) => setPartnerId(event.target.value)}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="">{t('common.select')}</option>
            {partners.map((partner) => (
              <option key={partner.id} value={partner.id}>
                {partner.code} — {partner.name}
              </option>
            ))}
          </select>
        </label>
        {partners.length === 0 && !error ? (
          <div className="text-muted-foreground">{t('order.transfer.no_targets')}</div>
        ) : null}
        {error ? <div className="text-red-600">{error}</div> : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !partnerId}
            className="rounded bg-primary px-3 py-1.5 text-primary-foreground disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('order.transfer.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
